import { addStoryMs, storyDeltaMs } from '../storyTime'
import type { ScenarioLifecycle } from '../types'

/** Idle, Running, Paused, Completed — numbers on the wire. */
export type StudioClockStatus = 'idle' | 'running' | 'paused' | 'completed'

export type StudioClock = {
  seq: number
  lifecycle: ScenarioLifecycle
  clockStatus: StudioClockStatus
  rate: number
  anchorStoryTimeIso: string | null
  anchorActualTime: string | null
  actualStartTime: string | null
  actualEndTime: string | null
  /**
   * Client-only. The server anchor from when this run began.
   * A shifted in-memory board can start later than the stored session; this
   * keeps pause/resume elapsed measured from the original server anchor.
   */
  storyOriginIso?: string | null
  /** Exercise take phase from SessionJson (piggybacked on Clock for room sync). */
  exerciseTakeNumber?: number
  exerciseTakeStopped?: boolean
  /**
   * Take-pause freeze point (story ms). `null` means cleared.
   * Omitted only on legacy clocks that predate the field.
   */
  instantReplayPauseMs?: number | null
  /** Instant Replay tape advancing (spectators). */
  instantReplayPlaying?: boolean
  /** Instant Replay entered via scrub (not plain Pause). */
  instantReplayEngaged?: boolean
  /**
   * Bumped on intentional IR jumps (beat skip / scrub). Spectators snap when this
   * changes; cadence tape Seeks do not bump it.
   */
  instantReplayAlignSeq?: number
}

const LIFECYCLE: ScenarioLifecycle[] = ['planned', 'running', 'completed']
const CLOCK_STATUS: StudioClockStatus[] = ['idle', 'running', 'paused', 'completed']

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

function enumName<T extends string>(value: unknown, table: readonly T[], fallback: T): T {
  if (typeof value === 'number' && table[value]) return table[value]
  if (typeof value === 'string') {
    const text = value.trim()
    const key = text.toLowerCase() as T
    if (table.includes(key)) return key
    if (/^\d+$/.test(text)) {
      const index = Number(text)
      if (table[index]) return table[index]
    }
  }
  return fallback
}

function isoOrNull(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null
  return value
}

/** Actual-clock fields are UTC. A zone-less value is UTC that lost its Z on the way out of SQL. */
function utcIsoOrNull(value: unknown): string | null {
  const text = isoOrNull(value)
  if (!text) return null
  if (/[zZ]$/.test(text) || /[+-]\d{2}:?\d{2}$/.test(text)) return text
  return `${text}Z`
}

function optionalInt(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.floor(value)
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) return Number(value.trim())
  return undefined
}

function optionalBool(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value
  if (value === 'true' || value === 1) return true
  if (value === 'false' || value === 0) return false
  return undefined
}

export function parseStudioClock(raw: unknown): StudioClock {
  const rec = asRecord(raw) ?? {}
  const takeNumber = optionalInt(rec.exerciseTakeNumber)
  const takeStopped = optionalBool(rec.exerciseTakeStopped)
  const hasReplayKey = Object.prototype.hasOwnProperty.call(rec, 'instantReplayPauseMs')
  const replayPause =
    rec.instantReplayPauseMs === null
      ? null
      : optionalInt(rec.instantReplayPauseMs)
  const hasPlayingKey = Object.prototype.hasOwnProperty.call(rec, 'instantReplayPlaying')
  const replayPlaying = optionalBool(rec.instantReplayPlaying)
  const hasEngagedKey = Object.prototype.hasOwnProperty.call(rec, 'instantReplayEngaged')
  const replayEngaged = optionalBool(rec.instantReplayEngaged)
  const replayAlignSeq = optionalInt(rec.instantReplayAlignSeq)
  return {
    seq: Number(rec.seq ?? 0) || 0,
    lifecycle: enumName(rec.lifecycle, LIFECYCLE, 'planned'),
    clockStatus: enumName(rec.clockStatus, CLOCK_STATUS, 'idle'),
    rate: Number(rec.rate ?? 1) || 1,
    anchorStoryTimeIso: isoOrNull(rec.anchorStoryTimeIso),
    anchorActualTime: utcIsoOrNull(rec.anchorActualTime),
    actualStartTime: utcIsoOrNull(rec.actualStartTime),
    actualEndTime: utcIsoOrNull(rec.actualEndTime),
    ...(takeNumber != null ? { exerciseTakeNumber: Math.max(0, takeNumber) } : {}),
    ...(takeStopped != null ? { exerciseTakeStopped: takeStopped } : {}),
    ...(hasReplayKey
      ? { instantReplayPauseMs: replayPause != null ? Math.max(0, replayPause) : null }
      : {}),
    ...(hasPlayingKey && replayPlaying != null ? { instantReplayPlaying: replayPlaying } : {}),
    ...(hasEngagedKey && replayEngaged != null ? { instantReplayEngaged: replayEngaged } : {}),
    ...(replayAlignSeq != null ? { instantReplayAlignSeq: Math.max(0, replayAlignSeq) } : {}),
  }
}

/**
 * Running: anchor story time plus actual elapsed × rate.
 * Anything else freezes on the anchor, or the story start when the anchor is empty.
 */
export function playheadIsoFromClock(clock: StudioClock, storyStartTime: string): string {
  const anchor = clock.anchorStoryTimeIso || storyStartTime
  if (clock.clockStatus === 'running' && clock.anchorActualTime) {
    const actualMs = Date.parse(clock.anchorActualTime)
    if (Number.isFinite(actualMs)) {
      const delta = Math.max(0, Date.now() - actualMs)
      const rate = clock.rate > 0 ? clock.rate : 1
      return addStoryMs(anchor, delta * rate)
    }
  }
  return anchor
}

/**
 * Playhead for the board on screen.
 * When the server anchor is still on an earlier story origin than this board's
 * start, the raw delta is negative and the clock would sit at 0. Count the
 * server's own story elapsed onto this board's start instead.
 */
export function boardPlayheadIso(clock: StudioClock, storyStartTime: string): string {
  const serverPlayhead = playheadIsoFromClock(clock, clock.anchorStoryTimeIso || storyStartTime)
  if (storyDeltaMs(storyStartTime, serverPlayhead) >= -1000) return serverPlayhead
  const origin = clock.storyOriginIso || clock.anchorStoryTimeIso || storyStartTime
  const storyElapsed = Math.max(0, storyDeltaMs(origin, serverPlayhead))
  return addStoryMs(storyStartTime, storyElapsed)
}
